/**
 * Version Check Utility for Web App Auto-Refresh
 * 
 * Auto-refreshes browser when backend version is ahead of frontend version.
 * Checks /v3/minVersion endpoint, prevents refresh loops, and handles errors gracefully.
 */

const LAST_FORCED_VERSION_KEY = 'lastForcedVersion';

/**
 * Compare two semantic version strings (e.g., "1.2.3" vs "1.3.0")
 * @returns -1 if a < b, 0 if equal, 1 if a > b
 */
function compareVersions(a: string, b: string): number {
  const pa = a.split('.');
  const pb = b.split('.');
  
  for (let i = 0; i < 3; i++) {
    const na = Number(pa[i] || 0);
    const nb = Number(pb[i] || 0);
    
    if (na > nb) return 1;
    if (nb > na) return -1;
  }
  
  return 0;
}

/**
 * Get the current UI version from version.json
 */
async function getUIVersion(): Promise<string> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch version.json: ${response.status}`);
    }
    const data = await response.json();
    return data.version || '0.0.0';
  } catch (error) {
    console.error('Error fetching UI version:', error);
    return process.env.REACT_APP_VERSION || '0.0.0';
  }
}

/**
 * Get version information from the API
 */
async function getVersionInfo(baseUrl: string): Promise<{ minVersion: string; currentVersion: string }> {
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  
  const response = await fetch(`${cleanBaseUrl}/v3/minVersion`, {
    credentials: 'include',
    mode: 'cors',
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch version info: ${response.status}`);
  }
  
  const data = await response.json();
  const devicePlatform = String((window as any).device?.platform || '').toLowerCase();
  const platformMinimum = devicePlatform === 'ios'
    ? data.minimumVersions?.ios
    : devicePlatform === 'android'
      ? data.minimumVersions?.android
      : null;
  return {
    minVersion: platformMinimum || data.minVersion || data.version || '0.0.0',
    currentVersion: data.currentVersion || data.version || '0.0.0',
  };
}

/**
 * Force a browser refresh with cache-busting
 */
function forceRefresh(currentVersion: string): void {
  console.log(`Forcing refresh for version ${currentVersion}`);
  
  localStorage.setItem(LAST_FORCED_VERSION_KEY, currentVersion);
  
  const keysToPreserve = ['userAuth', 'userAccount', 'persist:root'];
  const storageSnapshot: { [key: string]: string | null } = {};
  
  keysToPreserve.forEach(key => {
    storageSnapshot[key] = localStorage.getItem(key);
  });
  
  const currentPath = window.location.pathname;
  let currentSearch = window.location.search;
  
  currentSearch = currentSearch.replace(/[?&]v=[^&]*/g, '');
  const separator = currentSearch ? '&' : '?';
  const cacheBustUrl = `${currentPath}${currentSearch}${separator}v=${currentVersion}`;
  
  window.location.replace(cacheBustUrl);
}

/**
 * Check if the UI version matches the backend current version
 * If not, force a refresh (with loop prevention)
 * 
 * @param baseUrl - API base URL (e.g., 'https://api.tempstars.com')
 * @param skipIfRecentlyRefreshed - If true, skip check if we already refreshed for this version
 * @returns Promise<boolean> - true if version is OK, false if refresh was triggered
 */
export async function checkVersionAndRefresh(
  baseUrl: string,
  skipIfRecentlyRefreshed: boolean = true
): Promise<boolean> {
  try {
    console.log('Checking version compatibility...');
    
    const [uiVersion, versionInfo] = await Promise.all([
      getUIVersion(),
      getVersionInfo(baseUrl)
    ]);
    
    const { minVersion, currentVersion } = versionInfo;
    
    console.log(`UI Version: ${uiVersion}`);
    console.log(`Min Required Version: ${minVersion}`);
    console.log(`Current Deployed Version: ${currentVersion}`);
    
    // Check if UI version is less than current deployed version
    const isOutdated = compareVersions(uiVersion, currentVersion) === -1;
    
    // Also check if below minimum (safety check)
    const isBelowMinimum = compareVersions(uiVersion, minVersion) === -1;
    const targetVersion = isBelowMinimum ? minVersion : currentVersion;
    
    if (!isOutdated && !isBelowMinimum) {
      console.log('Version is up to date');
      return true;
    }
    
    if (isBelowMinimum) {
      console.log('⚠️ Version is below minimum required - MUST update');
    } else {
      console.log('Version mismatch detected - newer version available');
    }
    
    if (skipIfRecentlyRefreshed) {
      const lastForcedVersion = localStorage.getItem(LAST_FORCED_VERSION_KEY);
      
      if (lastForcedVersion === targetVersion) {
        console.log(`Already refreshed for version ${targetVersion}, skipping to prevent loop`);
        console.log('If you need to test again, clear the flag: localStorage.removeItem("lastForcedVersion")');
        setTimeout(() => {
          console.log('Clearing lastForcedVersion flag after timeout');
          localStorage.removeItem(LAST_FORCED_VERSION_KEY);
        }, 60000);
        return true;
      }
    }
    
    console.log('Triggering forced refresh...');
    forceRefresh(targetVersion);
    
    return false;
    
  } catch (error) {
    console.error('Error during version check:', error);
    if ((window as any).TempStars?.Analytics?.track) {
      (window as any).TempStars.Analytics.track('version_check_error', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
    return true;
  }
}

/**
 * Reset the forced version flag (useful for testing)
 */
export function resetVersionCheckFlag(): void {
  localStorage.removeItem(LAST_FORCED_VERSION_KEY);
}

export default checkVersionAndRefresh;
